import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Loader2, ScanLine, Users, Eye, Wrench, Globe, TrendingUp, DollarSign, CreditCard, Percent,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0C1424] border border-slate-800 rounded-lg p-5">
      <div className="flex items-center gap-2 text-slate-400 text-sm">
        <Icon size={16} className="text-cyanbrand" /> {label}
      </div>
      <p className="font-heading text-3xl font-bold mt-2">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

const gradeColor = (g) => ({ A: "text-green-400", B: "text-cyan-400", C: "text-yellow-400", D: "text-orange-400", F: "text-red-400" }[g] || "text-slate-400");

export default function Admin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && !user.is_admin) return;
    api.get("/admin/analytics").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [user]);

  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  const t = data?.totals || {};
  return (
    <div className="min-h-screen text-white">
      <Navbar />
      <main className="pt-24 pb-20 max-w-6xl mx-auto px-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyanbrand">Analytics</p>
        <h1 className="font-heading text-3xl font-semibold mt-2">Business dashboard</h1>
        <p className="text-sm text-slate-400 mt-1">Who's coming through, what they're scanning, and where your leads are.</p>

        {loading ? (
          <div className="mt-8 text-slate-400 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : !data ? (
          <p className="mt-8 text-slate-400">No data yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              <Stat testid="stat-visits" icon={Eye} label="Site visits" value={t.visits_total} sub={`${t.visits_7d} in last 7 days`} />
              <Stat testid="stat-scans" icon={ScanLine} label="Total scans" value={t.total_scans} sub={`${t.scans_today} today · ${t.scans_7d} this week`} />
              <Stat testid="stat-signups" icon={Users} label="Signups" value={t.total_users} sub={`${t.new_users_7d} new this week`} />
              <Stat testid="stat-leads" icon={Wrench} label="Fix-it leads" value={t.total_leads} sub={`${t.leads_7d} this week`} />
            </div>

            {/* Revenue & conversions — the "are they buying?" section */}
            <h2 className="font-heading text-lg font-semibold mt-8 mb-3 flex items-center gap-2">
              <DollarSign size={18} className="text-green-400" /> Are they buying?
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat testid="stat-customers" icon={CreditCard} label="Paying customers" value={t.paying_customers} sub={`${t.new_customers_7d} new this week`} />
              <Stat testid="stat-mrr" icon={DollarSign} label="Monthly revenue (MRR)" value={`$${t.mrr}`} sub={`$${t.revenue_7d} collected this week`} />
              <Stat testid="stat-total-revenue" icon={TrendingUp} label="Total revenue" value={`$${t.total_revenue}`} sub="all-time collected" />
              <Stat testid="stat-conversion" icon={Percent} label="Signup → paid" value={`${t.signup_to_paid}%`} sub={`${t.visit_to_signup}% visit → signup`} />
            </div>
            {t.paying_customers === 0 && (
              <p className="mt-3 text-sm text-amber-400/90 bg-amber-500/10 border border-amber-500/20 rounded-md px-4 py-3" data-testid="no-sales-note">
                No paid customers yet. You have traffic — now drive it to checkout: add a clear "Start free trial / Upgrade" CTA and follow up with signups. (Real payments also show live in your Stripe Dashboard → Payments.)
              </p>
            )}

            <div className="mt-6 bg-[#0C1424] border border-slate-800 rounded-lg p-6" data-testid="traffic-chart">
              <div className="flex items-center gap-2 mb-4"><TrendingUp size={18} className="text-cyanbrand" />
                <h2 className="font-heading text-lg font-semibold">Visits & scans — last 14 days</h2></div>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.series}>
                  <defs>
                    <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#7c3aed" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#7c3aed" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#0C1424", border: "1px solid #1e293b", borderRadius: 8, color: "#fff" }} />
                  <Area type="monotone" dataKey="visits" stroke="#00E5FF" fill="url(#gv)" name="Visits" />
                  <Area type="monotone" dataKey="scans" stroke="#7c3aed" fill="url(#gs)" name="Scans" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <div className="bg-[#0C1424] border border-slate-800 rounded-lg p-6" data-testid="top-domains">
                <div className="flex items-center gap-2 mb-4"><Globe size={18} className="text-cyanbrand" />
                  <h2 className="font-heading text-lg font-semibold">Most-scanned websites</h2></div>
                {data.top_domains.length === 0 ? <p className="text-sm text-slate-500">No scans yet.</p> : (
                  <ul className="space-y-2">
                    {data.top_domains.map((d) => (
                      <li key={d.host} className="flex items-center justify-between text-sm border-b border-slate-800/60 pb-2">
                        <span className="text-slate-200 truncate">{d.host}</span>
                        <span className="text-slate-500">{d.scans} scan{d.scans > 1 ? "s" : ""} · <span className={gradeColor()}> {d.last_score}</span></span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="bg-[#0C1424] border border-slate-800 rounded-lg p-6" data-testid="recent-scans">
                <div className="flex items-center gap-2 mb-4"><ScanLine size={18} className="text-cyanbrand" />
                  <h2 className="font-heading text-lg font-semibold">Recent scans</h2></div>
                {data.recent_scans.length === 0 ? <p className="text-sm text-slate-500">No scans yet.</p> : (
                  <ul className="space-y-2">
                    {data.recent_scans.map((s, i) => (
                      <li key={i} className="flex items-center justify-between text-sm border-b border-slate-800/60 pb-2">
                        <span className="text-slate-200 truncate">{s.host}</span>
                        <span className="flex items-center gap-2">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${s.type === "free" ? "bg-slate-800 text-slate-400" : "bg-cyan-900/40 text-cyan-300"}`}>{s.type}</span>
                          <span className={`font-semibold ${gradeColor(s.grade)}`}>{s.grade}</span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {data.recent_purchases && data.recent_purchases.length > 0 && (
              <div className="mt-6 bg-[#0C1424] border border-slate-800 rounded-lg p-6" data-testid="recent-purchases">
                <div className="flex items-center gap-2 mb-4"><DollarSign size={18} className="text-green-400" />
                  <h2 className="font-heading text-lg font-semibold">Recent purchases</h2></div>
                <ul className="space-y-2">
                  {data.recent_purchases.map((p, i) => (
                    <li key={i} className="flex items-center justify-between text-sm border-b border-slate-800/60 pb-2">
                      <span className="text-slate-200 truncate">{p.email}</span>
                      <span className="flex items-center gap-3">
                        <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-900/40 text-cyan-300 capitalize">{p.plan_id}</span>
                        <span className="text-green-400 font-semibold">${p.amount}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.top_referrers.length > 0 && (
              <div className="mt-6 bg-[#0C1424] border border-slate-800 rounded-lg p-6" data-testid="top-referrers">
                <h2 className="font-heading text-lg font-semibold mb-4">Where visitors come from</h2>
                <ul className="space-y-2">
                  {data.top_referrers.map((r) => (
                    <li key={r.referrer} className="flex items-center justify-between text-sm border-b border-slate-800/60 pb-2">
                      <span className="text-slate-300 truncate">{r.referrer}</span>
                      <span className="text-slate-500">{r.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
